/**
 * Shared runtime contract for the Kundali / birth-details form.
 *
 * Used by:
 *   - apps/mobile/components/kundali/BirthForm.tsx
 *   - apps/web/src/components/BirthDetailsForm.tsx
 *
 * These seven fields do not need a general-purpose schema runtime. Keeping this
 * package dependency-free prevents a large validation library from being
 * duplicated across browser chunks while preserving one canonical validator
 * for web and mobile.
 */

const MIN_BIRTH_YEAR = 1900;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const GENDERS = new Set(["male", "female", "other"]);

export interface KundaliFormInput {
  name: string;
  birthDate: string;
  birthTime: string | null;
  birthPlace: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  gender?: "male" | "female" | "other";
  language?: string;
}

export interface ValidationIssue {
  path: string[];
  message: string;
}

export type SafeParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: { issues: ValidationIssue[] } };

export interface SafeParseSchema<T> {
  safeParse(input: unknown): SafeParseResult<T>;
}

function failure(path: string, message: string): SafeParseResult<never> {
  return { success: false, error: { issues: [{ path: [path], message }] } };
}

function stringValue(input: unknown): string | null {
  return typeof input === "string" ? input.trim() : null;
}

function localDateKey(date: Date): number {
  return date.getFullYear() * 10_000 + (date.getMonth() + 1) * 100 + date.getDate();
}

function parseBirthDate(input: unknown): SafeParseResult<string> {
  const value = stringValue(input);
  const match = value?.match(DATE_PATTERN);
  if (!value || !match) return failure("birthDate", "Please enter a valid date (YYYY-MM-DD)");

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  const isCalendarDate =
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day;
  if (!isCalendarDate) return failure("birthDate", "Please enter a valid date");
  if (year < MIN_BIRTH_YEAR) {
    return failure("birthDate", `Birth year must be ${MIN_BIRTH_YEAR} or later`);
  }
  if (year * 10_000 + month * 100 + day > localDateKey(new Date())) {
    return failure("birthDate", "Birth date cannot be in the future");
  }
  return { success: true, data: value };
}

function parseBirthTime(input: unknown): SafeParseResult<string | null> {
  if (input === null) return { success: true, data: null };
  const value = stringValue(input);
  if (value && TIME_PATTERN.test(value)) return { success: true, data: value };
  return failure("birthTime", "Please enter a valid time (HH:MM)");
}

function parseBirthPlace(input: unknown): SafeParseResult<string> {
  const value = stringValue(input);
  if (value && value.length >= 2) return { success: true, data: value };
  return failure("birthPlace", "Please enter your birth place");
}

function parseBoundedNumber(
  input: unknown,
  path: "latitude" | "longitude",
  min: number,
  max: number
): SafeParseResult<number | undefined> {
  if (input === undefined) return { success: true, data: undefined };
  if (typeof input === "number" && Number.isFinite(input) && input >= min && input <= max) {
    return { success: true, data: input };
  }
  return failure(path, `${path} must be between ${min} and ${max}`);
}

function parseOptionalString(input: unknown, path: string): SafeParseResult<string | undefined> {
  if (input === undefined) return { success: true, data: undefined };
  if (typeof input === "string") return { success: true, data: input };
  return failure(path, `${path} must be a string`);
}

function parseOptionalIanaTimezone(input: unknown): SafeParseResult<string | undefined> {
  if (input === undefined) return { success: true, data: undefined };
  const value = stringValue(input);
  if (!value) return { success: true, data: undefined };
  if (value !== "UTC" && !value.includes("/")) {
    return failure("timezone", "timezone must be an IANA identifier such as Asia/Kolkata");
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
  } catch {
    return failure("timezone", "timezone must be an IANA identifier such as Asia/Kolkata");
  }
  return { success: true, data: value };
}

/** YYYY-MM-DD, a real calendar date, not in the future, and not before 1900. */
export const birthDateSchema: SafeParseSchema<string> = {
  safeParse: parseBirthDate,
};

/** HH:MM in 24-hour format, or null for "unknown time". */
export const birthTimeSchema: SafeParseSchema<string | null> = {
  safeParse: parseBirthTime,
};

/** Non-empty place name; city autocomplete resolves lat/lng separately. */
export const birthPlaceSchema: SafeParseSchema<string> = {
  safeParse: parseBirthPlace,
};

/** Canonical camelCase form validator shared by web and mobile. */
export const kundaliFormSchema: SafeParseSchema<KundaliFormInput> = {
  safeParse(input: unknown): SafeParseResult<KundaliFormInput> {
    if (!input || typeof input !== "object" || Array.isArray(input)) {
      return failure("form", "Please check your birth details");
    }
    const value = input as Record<string, unknown>;
    const name = stringValue(value.name);
    if (!name) return failure("name", "Please enter your name");
    if (name.length > 100) return failure("name", "Name must be 100 characters or fewer");

    const birthDate = parseBirthDate(value.birthDate);
    if (!birthDate.success) return birthDate;
    const birthTime = parseBirthTime(value.birthTime);
    if (!birthTime.success) return birthTime;
    const birthPlace = parseBirthPlace(value.birthPlace);
    if (!birthPlace.success) return birthPlace;
    const latitude = parseBoundedNumber(value.latitude, "latitude", -90, 90);
    if (!latitude.success) return latitude;
    const longitude = parseBoundedNumber(value.longitude, "longitude", -180, 180);
    if (!longitude.success) return longitude;
    const timezone = parseOptionalIanaTimezone(value.timezone);
    if (!timezone.success) return timezone;
    const language = parseOptionalString(value.language, "language");
    if (!language.success) return language;

    let gender: KundaliFormInput["gender"];
    if (value.gender !== undefined) {
      if (typeof value.gender !== "string" || !GENDERS.has(value.gender)) {
        return failure("gender", "gender must be male, female, or other");
      }
      gender = value.gender as KundaliFormInput["gender"];
    }

    return {
      success: true,
      data: {
        name,
        birthDate: birthDate.data,
        birthTime: birthTime.data,
        birthPlace: birthPlace.data,
        ...(latitude.data !== undefined && { latitude: latitude.data }),
        ...(longitude.data !== undefined && { longitude: longitude.data }),
        ...(timezone.data !== undefined && { timezone: timezone.data }),
        ...(gender !== undefined && { gender }),
        ...(language.data !== undefined && { language: language.data }),
      },
    };
  },
};

/**
 * Convert validated form input to the backend-ready KundaliRequest shape.
 * Optional location fields only pass through after autocomplete resolution.
 */
export function toKundaliRequest(input: KundaliFormInput) {
  return {
    name: input.name,
    birth_date: input.birthDate,
    birth_time: input.birthTime,
    birth_place: input.birthPlace,
    ...(input.latitude != null && { latitude: input.latitude }),
    ...(input.longitude != null && { longitude: input.longitude }),
    ...(input.timezone != null && { timezone: input.timezone }),
    ...(input.gender != null && { gender: input.gender }),
    ...(input.language != null && { language: input.language }),
  };
}
